import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, Truck, FileWarning, Wrench, ClipboardList, Award, X } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const ICONS = {
  doc_expiring: FileWarning,
  doc_expired: FileWarning,
  vehicle_assigned: Truck,
  order_created: ClipboardList,
  order_completed: Award,
  alert_pass: Bell,
};

const ICON_COLOR = {
  doc_expiring: "text-yellow-600",
  doc_expired: "text-red-600",
  vehicle_assigned: "text-blue-600",
  order_created: "text-orange-600",
  order_completed: "text-green-600",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      setData(r.data);
    } catch {
      setData({ items: [], unread: 0 });
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(t);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const onClickItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const removeNotif = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`);
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative h-12 w-12 flex items-center justify-center hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
        data-testid="notification-bell"
        aria-label="Notificaciones"
      >
        <Bell size={20} strokeWidth={2.5} />
        {data.unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute top-1 right-1 min-w-[20px] h-5 px-1 bg-orange-600 text-white text-[10px] font-bold flex items-center justify-center font-mono"
          >
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[92vw] bg-white border-2 border-black shadow-lg z-50"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <p className="veta-tag">Notificaciones</p>
              <p className="text-sm font-bold text-black mt-0.5">
                {data.unread} sin leer
              </p>
            </div>
            {data.unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs font-bold uppercase tracking-wider text-blue-600 underline flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <Check size={12} strokeWidth={2.5} /> Marcar leídas
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                <Bell size={28} strokeWidth={2} className="mx-auto mb-2 text-gray-300" />
                Sin notificaciones aún.
              </div>
            ) : (
              data.items.map((n) => {
                const Icon = ICONS[n.kind] || Bell;
                const color = ICON_COLOR[n.kind] || "text-gray-600";
                return (
                  <button
                    key={n.id}
                    onClick={() => onClickItem(n)}
                    className={`w-full text-left p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                      !n.read ? "bg-blue-50/40" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <Icon size={18} strokeWidth={2.5} className={`${color} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.read ? "font-bold text-black" : "text-gray-700"}`}>
                        {n.title}
                      </p>
                      {n.body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>}
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-1 font-mono">
                        {fmtDateTime(n.created_at)}
                      </p>
                    </div>
                    {!n.read && <span className="w-2 h-2 bg-blue-600 mt-1.5 flex-shrink-0" />}
                    <button
                      onClick={(e) => removeNotif(e, n.id)}
                      className="text-gray-400 hover:text-red-600 p-1"
                      aria-label="Eliminar"
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
